import { useCallback, useEffect, useState } from 'react';
import { Card, Button, Input, Badge } from '@/backoffice/components/ui';
import {
  fetchCountries,
  fetchRegions,
  fetchPrefectures,
  fetchCommunes,
  fetchDistricts,
  fetchSecteurs,
  createGeoEntity,
  deleteGeoEntity,
  type GeoItem,
} from '@/domains/api/geography.api';
import { ChevronRight, Plus, Trash2, MapPin, Loader2, Pencil, Check, X } from 'lucide-react';
import { updateGeoEntity } from '@/domains/api/geography.api';

type Level = 'countries' | 'regions' | 'prefectures' | 'communes' | 'districts' | 'secteurs';

interface PathItem {
  level: Level;
  id: number;
  name: string;
}

const LEVEL_LABELS: Record<Level, string> = {
  countries: 'Pays',
  regions: 'Régions',
  prefectures: 'Préfectures',
  communes: 'Communes',
  districts: 'Quartiers',
  secteurs: 'Secteurs',
};

const CHILD_LEVEL: Partial<Record<Level, Level>> = {
  countries: 'regions',
  regions: 'prefectures',
  prefectures: 'communes',
  communes: 'districts',
  districts: 'secteurs',
};

export function AdminGeographyManager() {
  const [path, setPath] = useState<PathItem[]>([]);
  const [items, setItems] = useState<GeoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [editing, setEditing] = useState<{ level: Level; item: GeoItem } | null>(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editLat, setEditLat] = useState('');
  const [editLng, setEditLng] = useState('');

  const currentLevel = (): Level => {
    if (path.length === 0) return 'countries';
    const last = path[path.length - 1];
    return CHILD_LEVEL[last.level] ?? 'secteurs';
  };

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (path.length === 0) {
        const { countries } = await fetchCountries();
        setItems(countries);
      } else {
        const last = path[path.length - 1];
        let list: GeoItem[] = [];
        switch (last.level) {
          case 'countries':
            list = await fetchRegions(last.id);
            break;
          case 'regions':
            list = await fetchPrefectures(last.id);
            break;
          case 'prefectures':
            list = await fetchCommunes(last.id);
            break;
          case 'communes':
            list = await fetchDistricts(last.id);
            break;
          case 'districts':
            list = await fetchSecteurs(last.id);
            break;
          default:
            list = [];
        }
        setItems(list);
      }
    } catch {
      setError('Impossible de charger les données géographiques.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const drillDown = (item: GeoItem) => {
    const parentLevel: Level = path.length === 0 ? 'countries' : path[path.length - 1].level;
    const childLevel = CHILD_LEVEL[parentLevel];
    if (!childLevel) return;
    setPath((p) => [...p, { level: childLevel, id: item.id, name: item.name }]);
    setNewName('');
    setNewCode('');
  };

  const goToIndex = (index: number) => {
    setPath((p) => p.slice(0, index));
    setNewName('');
    setNewCode('');
  };

  const parentPayload = (): Record<string, unknown> => {
    if (path.length === 0) return {};
    const last = path[path.length - 1];
    const map: Record<Level, string> = {
      countries: 'country_id',
      regions: 'region_id',
      prefectures: 'prefecture_id',
      communes: 'commune_id',
      districts: 'district_id',
      secteurs: 'district_id',
    };
    return { [map[last.level]]: last.id };
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const level = currentLevel();
      const payload: Record<string, unknown> = {
        name: newName.trim(),
        code: newCode.trim() || (level === 'countries' ? newName.trim().slice(0, 3).toUpperCase() : null),
        ...parentPayload(),
      };
      await createGeoEntity(level, payload);
      setNewName('');
      setNewCode('');
      await loadItems();
    } catch {
      setError('Échec de la création.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (level: Level, id: number) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    try {
      await deleteGeoEntity(level, id);
      await loadItems();
    } catch {
      setError('Suppression impossible (éléments liés existants).');
    }
  };

  const startEdit = (level: Level, item: GeoItem) => {
    setEditing({ level, item });
    setEditName(item.name);
    setEditCode(item.code ?? '');
    setEditLat(item.latitude != null ? String(item.latitude) : '');
    setEditLng(item.longitude != null ? String(item.longitude) : '');
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditName('');
    setEditCode('');
    setEditLat('');
    setEditLng('');
  };

  const handleUpdate = async () => {
    if (!editing || !editName.trim()) return;
    setSaving(true);
    try {
      await updateGeoEntity(editing.level, editing.item.id, {
        name: editName.trim(),
        code: editCode.trim() || null,
        latitude: editLat ? Number(editLat) : null,
        longitude: editLng ? Number(editLng) : null,
      });
      cancelEdit();
      await loadItems();
    } catch {
      setError('Échec de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  const level = currentLevel();
  const canDrill = level !== 'secteurs';

  return (
    <div className="space-y-6">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button type="button" onClick={() => goToIndex(0)} className="font-medium text-indigo-600 hover:underline">
          Tous les pays
        </button>
        {path.map((item, index) => (
          <span key={`${item.level}-${item.id}`} className="flex items-center gap-2">
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <button type="button" onClick={() => goToIndex(index + 1)} className="font-medium text-indigo-600 hover:underline">
              {item.name}
            </button>
          </span>
        ))}
        {path.length > 0 && (
          <>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">{LEVEL_LABELS[level]}</span>
          </>
        )}
      </div>

      <Card>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-indigo-600" />
          Ajouter {path.length === 0 ? 'un pays' : `une ${LEVEL_LABELS[level].slice(0, -1).toLowerCase()}`}
          {path.length > 0 && <span className="text-sm font-normal text-gray-500">dans {path[path.length - 1].name}</span>}
        </h3>
        <div className="grid md:grid-cols-3 gap-3">
          <Input placeholder="Nom *" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Input placeholder="Code (optionnel)" value={newCode} onChange={(e) => setNewCode(e.target.value)} />
          <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreate}>
            Ajouter
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => {
            const itemLevel = path.length === 0 ? 'countries' : level;
            const isEditing = editing?.item.id === item.id && editing.level === itemLevel;

            return (
              <Card key={item.id} className="hover:border-indigo-200 transition-colors">
                {isEditing ? (
                  <div className="space-y-3">
                    <Input placeholder="Nom *" value={editName} onChange={(e) => setEditName(e.target.value)} />
                    <Input placeholder="Code" value={editCode} onChange={(e) => setEditCode(e.target.value)} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Latitude" value={editLat} onChange={(e) => setEditLat(e.target.value)} />
                      <Input placeholder="Longitude" value={editLng} onChange={(e) => setEditLng(e.target.value)} />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" leftIcon={<Check className="w-4 h-4" />} isLoading={saving} onClick={handleUpdate}>
                        Enregistrer
                      </Button>
                      <Button size="sm" variant="outline" leftIcon={<X className="w-4 h-4" />} onClick={cancelEdit}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="flex-1 text-left"
                      onClick={() => canDrill && drillDown(item)}
                      disabled={!canDrill}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-indigo-600" />
                        <p className="font-medium">{item.name}</p>
                      </div>
                      {item.code && <Badge variant="default" size="sm">{item.code}</Badge>}
                      {(item.latitude || item.longitude) && (
                        <p className="text-xs text-gray-400 mt-2">{item.latitude}, {item.longitude}</p>
                      )}
                      {canDrill && (
                        <p className="text-xs text-indigo-600 mt-2">
                          Cliquer pour voir {LEVEL_LABELS[CHILD_LEVEL[itemLevel]!]?.toLowerCase()}
                        </p>
                      )}
                    </button>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(itemLevel, item)}
                        className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(itemLevel, item.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {!loading && items.length === 0 && (
        <Card><p className="text-center text-gray-500 py-8">Aucun élément. Ajoutez-en un ci-dessus.</p></Card>
      )}
    </div>
  );
}
